import LogRocket from 'logrocket';

// LogRocket configuration and utilities
class LogRocketService {
  private initialized = false;

  /**
   * Initialize LogRocket with the provided app ID
   * Should be called once at app startup
   */
  init(): void {
    const appId = import.meta.env.VITE_LOGROCKET_APP_ID;

    // Don't initialize in development unless explicitly enabled
    const isDev = import.meta.env.DEV;
    const enableInDev = import.meta.env.VITE_LOGROCKET_ENABLE_DEV === 'true';

    if (isDev && !enableInDev) {
      console.log('[LogRocket] Skipping initialization in development mode');
      return;
    }

    if (!appId) {
      console.warn('[LogRocket] App ID not found. LogRocket will not be initialized.');
      return;
    }

    // Check if LogRocket is available
    if (typeof LogRocket === 'undefined') {
      console.error(
        '[LogRocket] LogRocket script not loaded. Check network requests for *.logrocket.io'
      );
      return;
    }

    try {
      LogRocket.init(appId, {
        // Configure LogRocket options
        console: {
          isEnabled: {
            log: true,
            info: true,
            warn: true,
            error: true,
          },
          shouldAggregateConsoleErrors: true,
        },
        network: {
          isEnabled: true,
          requestSanitizer: request => {
            // Sanitize sensitive data from network requests
            if (request.headers?.authorization) {
              request.headers.authorization = '[REDACTED]';
            }
            if (request.headers?.apikey) {
              request.headers.apikey = '[REDACTED]';
            }
            return request;
          },
          responseSanitizer: response => {
            // Sanitize sensitive data from responses
            return response;
          },
        },
        dom: {
          isEnabled: true,
          baseHref: null,
        },
        shouldCaptureIP: false, // For privacy compliance
      });

      this.initialized = true;
      console.log('[LogRocket] Successfully initialized');
    } catch (error) {
      console.error('[LogRocket] Failed to initialize:', error);
    }
  }

  /**
   * Identify a user session with their email and additional attributes
   */
  identify(email: string, userAttributes?: Record<string, any>): void {
    if (!this.initialized) {
      // Silently skip identification when LogRocket is not initialized
      // This prevents console warnings in development mode
      return;
    }

    try {
      const attributes = {
        email,
        ...userAttributes,
      };

      LogRocket.identify(email, attributes);
      console.log('[LogRocket] User identified:', email);
    } catch (error) {
      console.error('[LogRocket] Failed to identify user:', error);
    }
  }

  /**
   * Track a custom event
   */
  track(eventName: string, properties?: Record<string, any>): void {
    if (!this.initialized) {
      // Silently skip tracking when LogRocket is not initialized
      return;
    }

    try {
      LogRocket.track(eventName, properties);
      console.log('[LogRocket] Event tracked:', eventName, properties);
    } catch (error) {
      console.error('[LogRocket] Failed to track event:', error);
    }
  }

  /**
   * Add custom metadata to the session (using track events)
   */
  addTag(name: string, value: string): void {
    if (!this.initialized) {
      // Silently skip tagging when LogRocket is not initialized
      return;
    }

    try {
      // Use track for custom session metadata
      this.track(`Session Tag: ${name}`, { [name]: value });
      console.log('[LogRocket] Tag tracked as event:', name, value);
    } catch (error) {
      console.error('[LogRocket] Failed to track tag:', error);
    }
  }

  /**
   * Capture additional context for the session
   */
  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
    if (!this.initialized) {
      // Silently skip message capture when LogRocket is not initialized
      return;
    }

    try {
      // Use track for custom messages instead of captureMessage
      LogRocket.track('Custom Message', { message, level });
    } catch (error) {
      console.error('[LogRocket] Failed to capture message:', error);
    }
  }

  /**
   * Get the current session URL for support purposes
   */
  getSessionURL(callback?: (url: string) => void): string | null {
    if (!this.initialized) {
      return null;
    }

    try {
      // LogRocket getSessionURL is async and requires a callback
      if (callback) {
        LogRocket.getSessionURL(callback);
        return null; // Async, result comes via callback
      } else {
        // For sync usage, we can't get URL without callback
        console.warn('[LogRocket] getSessionURL requires a callback function');
        return null;
      }
    } catch (error) {
      console.error('[LogRocket] Failed to get session URL:', error);
      return null;
    }
  }

  /**
   * Check if LogRocket is initialized and ready
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// Create and export a singleton instance
export const logRocketService = new LogRocketService();

// Export LogRocket directly for advanced usage
export { LogRocket };
